/**
 * Build-time discovery of blog posts.
 *
 * Posts are markdown files under ./posts/*.md with a YAML-ish frontmatter
 * block. We resolve them at build time via Vite's `import.meta.glob` (with
 * `query: '?raw'` so each module exports the raw markdown string), then
 * parse the frontmatter here so routes can list and look up posts by slug.
 */

type RawPostModule = { default: string };

const POST_MODULES = import.meta.glob<RawPostModule>('./posts/*.md', {
  eager: true,
  query: '?raw',
  import: 'default',
}) as unknown as Record<string, string>;

export type BlogPostFrontmatter = {
  title: string;
  date: string;
  summary: string;
  slug: string;
  author?: string;
  /** Optional chart slot key; when present, the chart is rendered above the body. */
  chart?: 'eval-spread';
};

export type BlogPost = {
  frontmatter: BlogPostFrontmatter;
  body: string;
  /** Filename slug derived from `posts/<slug>.md` when frontmatter omits it. */
  fileSlug: string;
};

const FRONTMATTER_RE = /^---\s*\n([\s\S]*?)\n---\s*\n?/;

const parseFrontmatter = (raw: string): { data: Record<string, string>; body: string } => {
  const match = FRONTMATTER_RE.exec(raw);
  if (!match) {
    return { data: {}, body: raw };
  }
  const data: Record<string, string> = {};
  for (const line of match[1].split('\n')) {
    const idx = line.indexOf(':');
    if (idx === -1) continue;
    const key = line.slice(0, idx).trim();
    let value = line.slice(idx + 1).trim();
    if (
      (value.startsWith('"') && value.endsWith('"')) ||
      (value.startsWith("'") && value.endsWith("'"))
    ) {
      value = value.slice(1, -1);
    }
    if (key) data[key] = value;
  }
  return { data, body: raw.slice(match[0].length) };
};

const buildPost = (path: string, raw: string): BlogPost => {
  const fileSlug = path.replace(/^.*\/posts\//, '').replace(/\.md$/, '');
  const { data, body } = parseFrontmatter(raw);
  const slug = data.slug || fileSlug;
  return {
    fileSlug,
    body,
    frontmatter: {
      title: data.title || fileSlug,
      date: data.date || '',
      summary: data.summary || '',
      slug,
      author: data.author,
      chart: data.chart === 'eval-spread' ? 'eval-spread' : undefined,
    },
  };
};

const POSTS: BlogPost[] = Object.entries(POST_MODULES)
  .map(([path, raw]) => buildPost(path, raw))
  .sort((a, b) => (a.frontmatter.date < b.frontmatter.date ? 1 : -1));

export const listPosts = (): BlogPost[] => POSTS;

export const getPostBySlug = (slug: string): BlogPost | undefined =>
  POSTS.find((post) => post.frontmatter.slug === slug);

const DATE_ONLY_RE = /^\d{4}-\d{2}-\d{2}$/;

// `new Date("2026-05-18")` is UTC midnight, which renders as the previous day
// for negative-offset timezones. Build the Date from local pieces instead.
export const formatPostDate = (iso: string): string => {
  if (!iso) return '';
  let d: Date;
  if (DATE_ONLY_RE.test(iso)) {
    const [year, month, day] = iso.split('-').map(Number);
    d = new Date(year, month - 1, day);
  } else {
    d = new Date(iso);
  }
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' });
};
